import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Upload, Save, Loader2, Image as ImageIcon } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { buildSignatureHtml } from "../lib/signature";

export function SignatureStudio({ companyId: initialCompanyId, onClose, onSaved }) {
  const { user, setUser } = useAuth();
  const { managed, refresh } = useCompany();
  const [companyId, setCompanyId] = useState(initialCompanyId || managed[0]?.id || "");
  const company = managed.find((c) => c.id === companyId) || {};

  const [member, setMember] = useState({
    avatar_url: user?.avatar_url || "", title: user?.title || "", phone: user?.phone || "",
  });
  const [comp, setComp] = useState({
    logo_url: company.logo_url || "", website: company.website || "", phone: company.phone || "", address: company.address || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const photoRef = useRef();
  const logoRef = useRef();

  // resync company fields when switching company
  const switchCompany = (id) => {
    setCompanyId(id);
    const c = managed.find((x) => x.id === id) || {};
    setComp({ logo_url: c.logo_url || "", website: c.website || "", phone: c.phone || "", address: c.address || "" });
  };

  const upload = async (file, kind) => {
    setUploading(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      const url = fileUrl(data.id);
      if (kind === "photo") setMember((m) => ({ ...m, avatar_url: url }));
      else setComp((c) => ({ ...c, logo_url: url }));
      toast.success(kind === "photo" ? "Photo ajoutée" : "Logo ajouté");
    } catch { toast.error("Échec de l'envoi"); } finally { setUploading(""); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const me = await api.patch("/profile", member);
      setUser?.(me.data);
      if (companyId) await api.patch(`/companies/${companyId}/profile`, comp);
      await refresh?.();
      toast.success("Signature enregistrée");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const previewUser = { ...user, ...member };
  const previewCompany = { ...company, ...comp };
  const html = buildSignatureHtml(previewUser, previewCompany);

  const node = (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-6" data-testid="signature-studio">
      <div className="bg-card border border-white/10 w-full sm:rounded-2xl shadow-2xl max-w-3xl h-[92vh] sm:h-auto sm:max-h-[88vh] flex flex-col overflow-hidden">
        <div className="h-14 shrink-0 flex items-center justify-between px-5 border-b border-white/10 bg-background">
          <span className="font-cabinet font-bold flex items-center gap-2"><ImageIcon size={18} className="text-klein" /> Studio de signature</span>
          <button data-testid="sigstudio-close" onClick={onClose} className="p-2 rounded-md hover:bg-white/[0.06] text-zinc-400"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Live preview — light + dark */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Aperçu (selon le fond du client mail)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-zinc-500 mb-1.5 flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-white border border-zinc-300" /> Fond clair</p>
                <div className="rounded-xl border border-white/10 bg-white p-4 overflow-x-auto" data-testid="sigstudio-preview" dangerouslySetInnerHTML={{ __html: html }} />
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 mb-1.5 flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#0d1117] border border-white/20" /> Fond sombre</p>
                <div className="rounded-xl border border-white/10 bg-[#0d1117] p-4 overflow-x-auto" data-testid="sigstudio-preview-dark" dangerouslySetInnerHTML={{ __html: html }} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Member */}
            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Vous</p>
              <div className="flex items-center gap-3">
                {member.avatar_url
                  ? <img src={member.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-klein" />
                  : <div className="w-14 h-14 rounded-full bg-white/[0.06] flex items-center justify-center text-zinc-500"><ImageIcon size={20} /></div>}
                <button data-testid="upload-photo" onClick={() => photoRef.current.click()} disabled={uploading === "photo"} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-white/10 hover:border-white/30">
                  {uploading === "photo" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Photo
                </button>
                <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "photo")} />
              </div>
              <Field label="Fonction / titre" testid="sig-title" value={member.title} onChange={(v) => setMember({ ...member, title: v })} placeholder="Gérant, Designer…" />
              <Field label="Téléphone" testid="sig-phone" value={member.phone} onChange={(v) => setMember({ ...member, phone: v })} placeholder="+33 6 12 34 56 78" />
            </div>

            {/* Company */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Entreprise</p>
                {managed.length > 1 && (
                  <select data-testid="sig-company" value={companyId} onChange={(e) => switchCompany(e.target.value)} className="text-xs bg-background border border-white/10 rounded-md px-2 py-1">
                    {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
              </div>
              <div className="flex items-center gap-3">
                {comp.logo_url
                  ? <img src={comp.logo_url} alt="" className="h-10 max-w-[120px] object-contain" />
                  : <div className="h-10 w-20 rounded-md bg-white/[0.06] flex items-center justify-center text-zinc-500"><ImageIcon size={18} /></div>}
                <button data-testid="upload-logo" onClick={() => logoRef.current.click()} disabled={uploading === "logo"} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-white/10 hover:border-white/30">
                  {uploading === "logo" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Logo
                </button>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "logo")} />
              </div>
              <Field label="Site web" testid="sig-website" value={comp.website} onChange={(v) => setComp({ ...comp, website: v })} placeholder="monsite.fr" />
              <Field label="Téléphone entreprise" testid="sig-cphone" value={comp.phone} onChange={(v) => setComp({ ...comp, phone: v })} placeholder="+33 1 23 45 67 89" />
              <Field label="Adresse" testid="sig-address" value={comp.address} onChange={(v) => setComp({ ...comp, address: v })} placeholder="12 rue X, 75000 Paris" />
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-end gap-2 shrink-0 bg-background">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/10 text-sm font-medium hover:bg-white/[0.06]">Annuler</button>
          <button data-testid="sigstudio-save" onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium hover:opacity-90">
            <Save size={15} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full mt-1 bg-background border border-white/10 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-klein" />
    </div>
  );
}
